import {
  Activity,
  CheckCircle2,
  CircleDashed,
  Clock3,
  GitBranch,
  RefreshCcw,
  ShieldAlert,
} from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  MetricCard,
  PageHeader,
  Spinner,
  cn,
} from '@helios/ui';
import { useBuildStatus } from '@/hooks/useBuildStatus';
import type { BuildStatusSprint, SprintState } from '@/services/buildStatusClient';
import { SettingsTableScroll } from '@/features/settings/settingsChrome';
import { DevelopmentChecklistPanel } from './DevelopmentChecklistPanel';
import { PageSectionWorkspace } from '@/components/layout/PageSectionWorkspace';

const statusMeta: Record<
  SprintState,
  { label: string; variant: 'success' | 'warning' | 'critical' | 'info' | 'outline' }
> = {
  completed: { label: 'Completed', variant: 'success' },
  in_progress: { label: 'In progress', variant: 'warning' },
  blocked: { label: 'Blocked', variant: 'critical' },
  next: { label: 'Next', variant: 'info' },
  outstanding: { label: 'Outstanding', variant: 'outline' },
};

export function BuildStatusPage() {
  return (
    <PageSectionWorkspace
      layout="tabs"
      ariaLabel="Build status sections"
      header={
        <PageHeader
          eyebrow="Platform development"
          title="Build Status"
          description="Track your master checklist, recorded progress, review evidence, and due items for this workspace."
        />
      }
      sections={[
        { id: 'checklist', label: 'Development checklist', content: <DevelopmentChecklistPanel /> },
        { id: 'roadmap', label: 'Repository roadmap', content: <RepositoryBuildStatus /> },
      ]}
    />
  );
}

function RepositoryBuildStatus() {
  const query = useBuildStatus();
  const status = query.data;
  const loading = query.isLoading;

  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <div className="border-border bg-card text-muted-foreground flex items-center gap-3 rounded-lg border px-4 py-3 text-sm">
          <Spinner className="text-primary h-4 w-4" aria-hidden="true" />
          Loading Engineering Bible build status…
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <ErrorState
        title="Build status unavailable"
        description="The protected build-status endpoint did not return a roadmap snapshot."
        action={
          <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
            <RefreshCcw className="h-4 w-4" aria-hidden="true" />
            Retry
          </Button>
        }
      />
    );
  }

  const generated = formatDateTime(status.generatedAt);
  const ledgerUpdated = status.sources.ledgerLastUpdated
    ? formatDateTime(status.sources.ledgerLastUpdated)
    : 'Unknown';

  return (
    <PageSectionWorkspace
      header={
        <PageHeader
          eyebrow="Engineering Bible records"
          title="Repository roadmap"
          description="Packaged sprint acceptance records from the Engineering Bible. These records do not independently verify deployed workflows or replace your development checklist."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="info" className="gap-1.5 py-1">
                <Activity className="h-3.5 w-3.5" aria-hidden="true" />
                Polls every 15s
              </Badge>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void query.refetch()}
              >
                <RefreshCcw className="h-4 w-4" aria-hidden="true" />
                Refresh
              </Button>
            </div>
          }
        />
      }
      toolbar={
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" aria-label="Build summary">
          <MetricCard
            label="Recorded accepted"
            value={status.summary.completed}
            unit={`/ ${status.summary.total}`}
            hint={`${status.summary.percentComplete}% of roadmap accepted`}
            accent="success"
            icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
          />
          <MetricCard
            label="In Progress"
            value={status.summary.inProgress}
            hint={
              status.nextSprint?.status === 'in_progress'
                ? status.nextSprint.sprintId
                : 'No active product sprint'
            }
            accent="warning"
            icon={<Clock3 className="h-4 w-4" aria-hidden="true" />}
          />
          <MetricCard
            label="Outstanding"
            value={status.summary.outstanding}
            hint={status.nextSprint ? `Next: ${status.nextSprint.sprintId}` : 'Roadmap complete'}
            accent="info"
            icon={<CircleDashed className="h-4 w-4" aria-hidden="true" />}
          />
          <MetricCard
            label="Blocked"
            value={status.summary.blocked}
            hint={
              status.summary.blocked === 0
                ? 'No blockers recorded'
                : 'Review ledger before continuing'
            }
            accent={status.summary.blocked === 0 ? 'success' : 'critical'}
            icon={<ShieldAlert className="h-4 w-4" aria-hidden="true" />}
          />
        </section>
      }
      minSectionWidth={420}
      sections={[
        {
          id: 'progress',
          label: 'Roadmap progress',
          content: (
            <Card className="min-w-0 xl:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Roadmap Progress</CardTitle>
                <CardDescription>
                  Source: {status.sources.roadmapPath}; ledger updated {ledgerUpdated}. Evidence
                  filenames refer to repository documents and may be absent from the packaged image.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-secondary mb-4 h-2 overflow-hidden rounded-full">
                  <div
                    className="bg-success h-full rounded-full"
                    style={{ width: `${status.summary.percentComplete}%` }}
                    aria-label={`${status.summary.percentComplete}% complete`}
                  />
                </div>
                <SprintTable sprints={status.sprints} />
              </CardContent>
            </Card>
          ),
        },
        {
          id: 'repository',
          label: 'Current repository',
          content: (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Current Repo State</CardTitle>
                <CardDescription>Generated {generated}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <InfoRow
                  label="Branch"
                  value={status.repo.branch ?? 'Unknown'}
                  icon={<GitBranch className="h-4 w-4" aria-hidden="true" />}
                />
                <InfoRow label="Commit" value={status.repo.commit ?? 'Unknown'} />
                <InfoRow
                  label="Worktree"
                  value={
                    status.repo.dirty
                      ? 'Uncommitted changes'
                      : status.repo.dirty === false
                        ? 'Clean'
                        : 'Unknown'
                  }
                />
                <InfoRow label="Remote" value={status.repo.remote ?? 'Unknown'} />
              </CardContent>
            </Card>
          ),
        },
        {
          id: 'next-slice',
          label: 'Next build slice',
          content: (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Next Build Slice</CardTitle>
                <CardDescription>
                  Determined from the first unaccepted roadmap sprint.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {status.nextSprint ? (
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-foreground text-sm font-semibold">
                          {status.nextSprint.title}
                        </p>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {status.nextSprint.sprintId}
                        </p>
                      </div>
                      <StatusBadge state={status.nextSprint.status} />
                    </div>
                    <p className="text-muted-foreground text-sm">{status.nextSprint.objective}</p>
                    <p className="text-muted-foreground break-all text-xs">
                      {status.nextSprint.specPath}
                    </p>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">All roadmap sprints are accepted.</p>
                )}
              </CardContent>
            </Card>
          ),
        },
      ]}
    />
  );
}

function SprintTable({ sprints }: { sprints: BuildStatusSprint[] }) {
  return (
    <SettingsTableScroll label="Sprint roadmap">
      <table className="w-full min-w-[860px] border-collapse text-left text-sm">
        <thead className="bg-card text-2xs text-muted-foreground uppercase tracking-wide">
          <tr>
            <th className="border-border w-16 border-b px-3 py-2 font-medium">Order</th>
            <th className="border-border w-72 border-b px-3 py-2 font-medium">Sprint</th>
            <th className="border-border border-b px-3 py-2 font-medium">Objective</th>
            <th className="border-border w-32 border-b px-3 py-2 font-medium">Status</th>
            <th className="border-border w-48 border-b px-3 py-2 font-medium">Evidence</th>
          </tr>
        </thead>
        <tbody>
          {sprints.map((sprint) => (
            <tr key={sprint.sprintId} className="border-border/70 border-b last:border-b-0">
              <td className="text-muted-foreground px-3 py-3 text-xs tabular-nums">
                S{sprint.order.toString().padStart(2, '0')}
              </td>
              <td className="px-3 py-3">
                <p className="text-foreground font-medium">{sprint.title}</p>
                <p className="text-muted-foreground mt-1 text-xs">{sprint.sprintId}</p>
              </td>
              <td className="text-muted-foreground px-3 py-3 text-sm">{sprint.objective}</td>
              <td className="px-3 py-3">
                <StatusBadge state={sprint.status} />
              </td>
              <td className="px-3 py-3">
                {sprint.evidence.length > 0 ? (
                  <ul className="space-y-1">
                    {sprint.evidence.map((item) => (
                      <li
                        key={item.path}
                        className={cn(
                          'break-all text-xs',
                          item.exists ? 'text-muted-foreground' : 'text-critical',
                        )}
                      >
                        {item.path}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted-foreground text-xs">Pending</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SettingsTableScroll>
  );
}

function StatusBadge({ state }: { state: SprintState }) {
  const meta = statusMeta[state];
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function InfoRow({ label, value, icon }: { label: string; value: string; icon?: ReactNode }) {
  return (
    <div className="border-border bg-background/40 flex items-start justify-between gap-3 rounded-lg border px-3 py-2">
      <span className="text-muted-foreground flex shrink-0 items-center gap-2 text-xs uppercase tracking-wide">
        {icon}
        {label}
      </span>
      <span className="text-foreground break-all text-right text-sm font-medium">{value}</span>
    </div>
  );
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}
